import { CaretDownIcon } from '@glu/icons-react';
import { createUseStyles, useDarkMode } from '@glu/theming';
import classnames from 'classnames';
import React, { KeyboardEvent, useCallback, useContext } from 'react';
import locale from '@glu/locale';
import { AccessibilityText } from '@glu/utilities-react';
import SelectString from './SelectString';
import GroupLabel from './GroupLabel';
import styles from './SelectField.styles';
import DropdownContext from './DropdownContext';

const useStyles = createUseStyles(styles);

const SelectField = () => {
  const dark = useDarkMode();
  const classes = useStyles();

  const {
    ariaLabel,
    divRef,
    dropdownActions: {
      applySelection,
      handleClose,
      onBlur,
      openDropdown,
      setFocusedIndex,
      setPressedKey,
      setSelection,
    },
    props: {
      dataQa,
      disabled,
      errors,
      filterEnabled,
      fitParentWidth,
      format,
      multiselect,
      placeholder,
      showBadges,
      showGroupInSelection,
      showSelected,
    },
    state: { open, parentDisabled, selection },
    uniqueId: htmlId,
  } = useContext(DropdownContext);

  const [error] = errors || [];
  const isDisabled = parentDisabled || disabled;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // alphanumeric keys
      const isAlphanumericKey = /[a-zA-Z0-9]/.test(event.key);

      switch (true) {
        case event.key === 'Escape':
          handleClose();
          event.stopPropagation();
          break;
        case event.key === 'ArrowDown':
          openDropdown(true);
          if (!filterEnabled) {
            setFocusedIndex(1);
          }
          break;
        case event.key === 'Delete':
        case event.key === 'Backspace':
          applySelection([]);
          break;
        case isAlphanumericKey:
          openDropdown(true);
          if (!filterEnabled) {
            setPressedKey(event.key);
          }
          break;
        default:
      }
    },
    [applySelection, filterEnabled, handleClose, openDropdown, setFocusedIndex, setPressedKey]
  );

  const group = selection[0]?.parentName;
  const getSelectFieldString = useCallback(() => {
    let selectionString = '';
    const selectionLength = selection.length;
    if (selectionLength) {
      if (selectionLength > showSelected) {
        selectionString = `${selectionLength} ${locale.get('selected')}`;
      } else {
        selectionString = format(selection);
      }
    }
    return selectionString;
  }, [format, selection, showSelected]);

  const handleBlur = useCallback(
    (e) => {
      if (!divRef.current.contains(e.relatedTarget)) {
        onBlur();
      }
    },
    [divRef, onBlur]
  );

  return (
    <div
      aria-activedescendant={
        selection[0]?.id && !multiselect
          ? `${htmlId}-category-option-button-${selection[0].id}`
          : null
      }
      aria-controls={`${htmlId}-dropdown-panel`}
      aria-expanded={open}
      aria-label={ariaLabel}
      role="combobox"
      tabIndex={0}
    >
      <AccessibilityText {...{ id: `${htmlId}-dropdown-label` }}>
        {locale.get('dropdown')}
      </AccessibilityText>
      <button
        aria-labelledby={`${htmlId}-select-field-label ${htmlId}-dropdown-label`}
        className={classnames(classes.input, {
          [classes.groupLabel]: showGroupInSelection,
          [classes.disabled]: isDisabled,
          [classes.dark]: dark,
          [classes.error]: !!error,
        })}
        data-qa={`${htmlId}-select-field`}
        disabled={isDisabled}
        id={`${htmlId}-select-field`}
        onBlur={handleBlur}
        onClick={() => {
          openDropdown(!open);
        }}
        onKeyDown={handleKeyDown}
        title={getSelectFieldString() || undefined}
        type="button"
      >
        {!multiselect && showGroupInSelection && (
          <GroupLabel>{group || locale.get('groupLabel')}</GroupLabel>
        )}
        <CaretDownIcon
          className={classnames(classes.caret, {
            [classes.disabled]: isDisabled,
            [classes.dark]: dark,
          })}
        />
      </button>
      <div
        className={classnames(classes.selection, {
          [classes.fitParentWidth]: fitParentWidth,
          [classes.dark]: dark,
        })}
        data-qa={dataQa ? `${dataQa}-selection` : `dropdown-${htmlId}-selection`}
        onKeyDown={handleKeyDown}
        role="presentation"
      >
        <SelectString
          applySelection={applySelection}
          disabled={isDisabled}
          format={format}
          onClick={() => {
            if (!isDisabled) {
              openDropdown(!open);
            }
          }}
          placeholder={placeholder}
          selection={selection}
          setSelection={setSelection}
          showBadges={showBadges}
          showSelected={showSelected}
        />
      </div>
    </div>
  );
};

export default SelectField;
